/* SmartVideo — reproductor único para todos los orígenes de vídeo.
   Regla de oro Fase 8: cero cambios de UX. Vídeos locales siguen siendo
   <video src>; Cloudflare Stream reproduce HLS adaptativo (hls.js, o nativo
   en Safari) con fallback iframe; YouTube/Vimeo no cambian (iframe embed). */
import { useEffect, useRef } from "react";

const isHls = (playback) => playback?.type === "hls" && playback?.src;
const isFrame = (playback) => (playback?.type === "iframe" || playback?.type === "embed") && playback?.embed_url;

export const SmartVideo = ({ playback, src, className = "", iframeTitle = "video", ...videoProps }) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const hlsSrc = isHls(playback) ? playback.src : null;

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !hlsSrc) return undefined;
    if (el.canPlayType("application/vnd.apple.mpegurl")) {
      el.src = hlsSrc; // Safari: HLS nativo
      return undefined;
    }
    let cancelled = false;
    import("hls.js").then(({ default: Hls }) => {
      if (cancelled || !Hls.isSupported()) return;
      const hls = new Hls({ maxBufferLength: 30 });
      hlsRef.current = hls;
      hls.loadSource(hlsSrc);
      hls.attachMedia(el);
    });
    return () => {
      cancelled = true;
      hlsRef.current?.destroy();
      hlsRef.current = null;
    };
  }, [hlsSrc]);

  if (isFrame(playback)) {
    return (
      <iframe
        src={playback.embed_url}
        title={iframeTitle}
        className={className}
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
        allowFullScreen
        data-testid="smart-video-embed"
      />
    );
  }
  return (
    <video
      ref={videoRef}
      className={className}
      data-testid="smart-video"
      {...(hlsSrc ? {} : { src: playback?.src || src })}
      {...videoProps}
    />
  );
};
